import React, { useContext, useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    useToast,
    Card,
    CardBody,
    Flex,
    SimpleGrid,
    Spinner,
    Button,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    useBreakpointValue,
    Badge,
    Tooltip,
    Divider,
} from '@chakra-ui/react';
import { RepeatIcon } from '@chakra-ui/icons';
import {
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    Cell,
    PieChart,
    Pie,
} from 'recharts';
import AuthContext from '../../context/AuthContext';
import ThemeContext from '../../context/ThemeContext';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

// Bias code to name mapping
const BIAS_CODES = {
    OVERCONF: 'Overconfidence',
    LOSSAV: 'Loss Aversion',
    ANCHOR: 'Anchoring',
    HERD: 'Herd Behavior',
    FAMILIAR: 'Familiarity Bias',
    CONFIRM: 'Confirmation Bias',
    RECENCY: 'Recency Bias',
    AFFECT: 'Affect Infusion',
    MENTAL: 'Mental Accounting',
    IMPACT: 'Impact Bias',
    DISPOS: 'Disposition Effect',
    COGNITIVE: 'Cognitive Dissonance',
    TAXAV: 'Tax Aversion',
    REGRET: 'Regret Aversion',
    EMOTION: 'Emotional State',
};

function DashboardPortfolioCustomerInsightsPaid() {
    const { token } = useContext(AuthContext);
    const { themeData } = useContext(ThemeContext);
    const [loading, setLoading] = useState(false);
    const [insightsData, setInsightsData] = useState(null);
    const toast = useToast();

    // Extract theme colors with fallbacks
    const primaryColor = themeData?.colors?.primary || '#0B2545';
    const secondaryColor = themeData?.colors?.secondary || '#13315C';
    const accentColor = themeData?.colors?.accent || '#13C1BB';

    // Determine if theme is dark (midnight)
    const isDarkTheme = themeData?.id === 'midnight';

    // Card background - improved for dark theme
    const cardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.6)' : 'white';

    // Text colors with proper contrast - following Dashboard.js pattern
    const headingTextColor = isDarkTheme ? '#F5F7FA' : primaryColor;
    const bodyTextColor = isDarkTheme ? '#E2E8F0' : 'gray.600';
    const mutedTextColor = isDarkTheme ? '#CBD5E0' : 'gray.500';
    const labelTextColor = isDarkTheme ? '#E2E8F0' : 'gray.700';

    // Border colors
    const borderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : 'gray.200';

    // Status colors - lighter for dark theme
    const successColor = isDarkTheme ? '#68D391' : '#38A169';
    const errorColor = isDarkTheme ? '#FC8181' : '#E53E3E';
    const warningColor = isDarkTheme ? '#F6AD55' : '#DD6B20';
    const purpleColor = isDarkTheme ? '#B794F4' : '#805AD5';

    // Info card backgrounds - improved transparency for dark theme
    const infoBg = isDarkTheme ? 'rgba(66, 153, 225, 0.15)' : accentColor + '15';
    const successBg = isDarkTheme ? 'rgba(72, 187, 120, 0.15)' : '#F0FFF4';
    const errorBg = isDarkTheme ? 'rgba(252, 129, 129, 0.15)' : '#FFF5F5';
    const purpleBg = isDarkTheme ? 'rgba(159, 122, 234, 0.15)' : '#FAF5FF';

    // Placeholder/empty background
    const placeholderBg = isDarkTheme ? 'rgba(45, 55, 72, 0.4)' : 'gray.50';

    // Button hover
    const buttonHoverBg = isDarkTheme ? accentColor : secondaryColor;

    // Color palettes for charts - theme aware with better visibility
    const AGE_COLORS = isDarkTheme 
        ? ['#13C1BB', '#68D391', '#B794F4', '#F6AD55', '#FC8181', '#90CDF4']
        : [primaryColor, successColor, purpleColor, warningColor, errorColor, '#D69E2E'];
    
    const SEGMENT_COLORS = isDarkTheme
        ? ['#13C1BB', '#68D391', '#B794F4', '#F6AD55', '#FC8181', '#90CDF4', '#4FD1C5', '#ED64A6']
        : [primaryColor, successColor, purpleColor, warningColor, errorColor, '#D69E2E', '#00B5D8', '#D53F8C'];

    // Responsive
    const isMobile = useBreakpointValue({ base: true, md: false });

    // Fetch insights data
    const fetchInsightsData = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API_BASE_URL}/content/admin-customer-insights`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setInsightsData(res.data);

            if (!res.data.bias_ranking || res.data.bias_ranking.length === 0) {
                toast({
                    title: 'No Data',
                    description: 'No customer insights available.',
                    status: 'info',
                    duration: 4000,
                    isClosable: true,
                    position: 'top',
                });
            }
        } catch (error) {
            console.error('Insights fetch error:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to load customer insights.',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
            setInsightsData(null);
        } finally {
            setLoading(false);
        }
    };

    // Initial load
    useEffect(() => {
        fetchInsightsData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Render pyramid chart for bias ranking
    const renderBiasPyramid = () => {
        if (!insightsData || !insightsData.bias_ranking || insightsData.bias_ranking.length === 0) {
            return (
                <Flex justify="center" align="center" py={10}>
                    <Text color={mutedTextColor}>No bias ranking data available.</Text>
                </Flex>
            );
        }
    
        // Sort by mean_score descending to create pyramid effect
        const sortedBiases = [...insightsData.bias_ranking].sort((a, b) => b.mean_score - a.mean_score);
        
        const pyramidData = sortedBiases.map((bias) => ({
            name: BIAS_CODES[bias.bias_code] || bias.bias_name,
            score: parseFloat(bias.mean_score.toFixed(2)),
            n: bias.n,
            bias_code: bias.bias_code,
        }));
    
        return (
            <Box>
                {/* Pyramid visual representation */}
                <VStack spacing={3} align="center" mb={6} position="relative">
                    {pyramidData.map((bias, index) => {
                        const maxWidth = 95;
                        const minWidth = 30;
                        const widthPercent = maxWidth - ((maxWidth - minWidth) * index / (pyramidData.length - 1));
                        const isTop = index === 0;
                        const isBottom = index === pyramidData.length - 1;
                        
                        // Color scheme based on score
                        const getColorScheme = (score) => {
                            if (score >= 70) return { bg: 'red.500', bgGradient: 'linear(to-r, red.500, red.600)', shadow: 'red' };
                            if (score >= 50) return { bg: 'orange.400', bgGradient: 'linear(to-r, orange.400, orange.500)', shadow: 'orange' };
                            if (score >= 30) return { bg: 'yellow.400', bgGradient: 'linear(to-r, yellow.400, yellow.500)', shadow: 'yellow' };
                            return { bg: 'green.400', bgGradient: 'linear(to-r, green.400, green.500)', shadow: 'green' };
                        };
                        
                        const colors = getColorScheme(bias.score);
                        
                        return (
                            <Box key={bias.bias_code} w="100%" display="flex" justifyContent="center">
                                <Tooltip
                                    label={
                                        <Box p={2}>
                                            <Text fontWeight="bold" fontSize="sm" mb={1}>{bias.name}</Text>
                                            <Divider my={1} />
                                            <HStack justify="space-between" fontSize="xs">
                                                <Text>Mean Score:</Text>
                                                <Text fontWeight="semibold">{bias.score}</Text>
                                            </HStack>
                                            <HStack justify="space-between" fontSize="xs">
                                                <Text>Sample Size:</Text>
                                                <Text fontWeight="semibold">{bias.n}</Text>
                                            </HStack>
                                            <HStack justify="space-between" fontSize="xs">
                                                <Text>Rank:</Text>
                                                <Text fontWeight="semibold">#{index + 1}</Text>
                                            </HStack>
                                        </Box>
                                    }
                                    placement="top"
                                    hasArrow
                                    bg={isDarkTheme ? 'gray.800' : 'gray.700'}
                                    color="white"
                                >
                                    <Box
                                        w={`${widthPercent}%`}
                                        bgGradient={colors.bgGradient}
                                        borderRadius={isTop ? "xl" : isBottom ? "lg" : "lg"}
                                        px={isMobile ? 3 : 5}
                                        cursor="pointer"
                                        position="relative"
                                        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                                        boxShadow={`0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)`}
                                        _hover={{
                                            transform: 'translateY(-4px) scale(1.02)',
                                            boxShadow: `0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04), 0 0 0 3px ${colors.shadow}.200`,
                                            zIndex: 10,
                                        }}
                                        _before={{
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            borderRadius: 'inherit',
                                            background: 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
                                            pointerEvents: 'none',
                                        }}
                                    >
                                        <Flex 
                                            justify="space-between" 
                                            align="center"
                                        >
                                            <Text
                                                fontSize={isMobile ? 'sm' : 'md'}
                                                fontWeight="bold"
                                                color="white"
                                                noOfLines={1}
                                                textShadow="0 1px 2px rgba(0,0,0,0.2)"
                                                flex="1"
                                            >
                                                {bias.name}
                                            </Text>
                                            <HStack spacing={isMobile ? 2 : 3} ml={2}>
                                                <VStack spacing={0} align="flex-end">
                                                    <Badge
                                                        bg="whiteAlpha.300"
                                                        color="white"
                                                        fontSize={isMobile ? 'xs' : 'sm'}
                                                        px={isMobile ? 2 : 3}
                                                        mt={1}
                                                        borderRadius="md"
                                                        fontWeight="bold"
                                                        border="1px solid"
                                                        borderColor="whiteAlpha.400"
                                                        backdropFilter="blur(4px)"
                                                        textShadow="0 1px 2px rgba(0,0,0,0.2)"
                                                    >
                                                        {bias.score.toFixed(0)}
                                                    </Badge>
                                                    <Text
                                                        fontSize={isMobile ? '9px' : '10px'}
                                                        color="whiteAlpha.800"
                                                        fontWeight="medium"
                                                        mt={0.5}
                                                        textShadow="0 1px 2px rgba(0,0,0,0.2)"
                                                    >
                                                        n={bias.n}
                                                    </Text>
                                                </VStack>
                                            </HStack>
                                        </Flex>

                                        {/* Bottom shine effect */}
                                        <Box
                                            position="absolute"
                                            bottom={0}
                                            left={0}
                                            right={0}
                                            h="2px"
                                            bg="blackAlpha.200"
                                            borderBottomRadius="inherit"
                                        />
                                    </Box>
                                </Tooltip>
                            </Box>
                        );
                    })}
                </VStack>

                {/* Legend */}
                <Flex 
                    justify="center" 
                    align="center" 
                    mt={6} 
                    flexWrap="wrap" 
                    gap={3}
                    bg={placeholderBg}
                    p={3}
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor={borderColor}
                >
                    <HStack spacing={2}>
                        <Box w="12px" h="12px" bgGradient="linear(to-r, green.400, green.500)" borderRadius="sm" />
                        <Text fontSize="xs" color={bodyTextColor}>Low (0-29)</Text>
                    </HStack>
                    <HStack spacing={2}>
                        <Box w="12px" h="12px" bgGradient="linear(to-r, yellow.400, yellow.500)" borderRadius="sm" />
                        <Text fontSize="xs" color={bodyTextColor}>Medium (30-49)</Text>
                    </HStack>
                    <HStack spacing={2}>
                        <Box w="12px" h="12px" bgGradient="linear(to-r, orange.400, orange.500)" borderRadius="sm" />
                        <Text fontSize="xs" color={bodyTextColor}>High (50-69)</Text>
                    </HStack>
                    <HStack spacing={2}>
                        <Box w="12px" h="12px" bgGradient="linear(to-r, red.500, red.600)" borderRadius="sm" />
                        <Text fontSize="xs" color={bodyTextColor}>Critical (70+)</Text>
                    </HStack>
                </Flex>
            </Box>
        );
    };

    // Render pie chart for age distribution
    const renderAgeDistribution = () => {
        if (!insightsData || !insightsData.age_distribution || insightsData.age_distribution.length === 0) {
            return (
                <Flex justify="center" align="center" py={10}>
                    <Text color={mutedTextColor}>No age distribution data available.</Text>
                </Flex>
            );
        }
    
        const ageData = insightsData.age_distribution.map((item, index) => ({
            name: item.bucket,
            value: item.count,
            percentage: item.percentage,
            color: AGE_COLORS[index % AGE_COLORS.length],
        }));
    
        return (
            <Box>
                {/* Clean Pie Chart - No labels, no legend */}
                <Flex justify="center" align="center" py={6}>
                    <ResponsiveContainer width="100%" height={isMobile ? 280 : 350}>
                        <PieChart>
                            <Pie
                                data={ageData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={false}
                                outerRadius={isMobile ? 100 : 140}
                                innerRadius={isMobile ? 50 : 70}
                                fill="#8884d8"
                                dataKey="value"
                                paddingAngle={2}
                                stroke={isDarkTheme ? 'rgba(45, 55, 72, 0.9)' : cardBg}
                                strokeWidth={3}
                            >
                                {ageData.map((entry, index) => (
                                    <Cell 
                                        key={`cell-${index}`} 
                                        fill={entry.color}
                                    />
                                ))}
                            </Pie>
                            <RechartsTooltip
                                contentStyle={{
                                    backgroundColor: isDarkTheme ? '#2D3748' : cardBg,
                                    border: `1px solid ${borderColor}`,
                                    borderRadius: '12px',
                                    padding: '12px 16px',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                    color: isDarkTheme ? '#E2E8F0' : '#1A202C',
                                }}
                                formatter={(value, name, props) => [
                                    `${value} customers (${props.payload.percentage.toFixed(1)}%)`,
                                    props.payload.name
                                ]}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </Flex>

                {/* Legend Cards Below Chart */}
                <VStack spacing={3} mt={6} w="100%">
                    {ageData.map((item, index) => (
                        <Box
                            key={index}
                            w="100%"
                            bg={cardBg}
                            borderRadius="lg"
                            border="1px solid"
                            borderColor={borderColor}
                            p={4}
                            transition="all 0.2s"
                            cursor="pointer"
                            _hover={{
                                borderColor: item.color,
                                boxShadow: `0 4px 12px ${item.color}33`,
                                transform: 'translateY(-2px)',
                            }}
                        >
                            <Flex justify="space-between" align="center">
                                <HStack spacing={3} flex="1">
                                    <Box 
                                        w={isMobile ? "40px" : "50px"} 
                                        h={isMobile ? "40px" : "50px"} 
                                        bg={item.color} 
                                        borderRadius="lg"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        fontWeight="bold"
                                        fontSize={isMobile ? "lg" : "xl"}
                                        color="white"
                                        boxShadow="md"
                                    >
                                        {item.name.split('-')[0]}
                                    </Box>
                                    <VStack align="start" spacing={0}>
                                        <Text 
                                            fontSize={isMobile ? "md" : "lg"} 
                                            fontWeight="bold" 
                                            color={labelTextColor}
                                        >
                                            {item.name}
                                        </Text>
                                        <Text fontSize="xs" color={mutedTextColor}>
                                            Age Range
                                        </Text>
                                    </VStack>
                                </HStack>
                                
                                <VStack align="end" spacing={0}>
                                    <HStack>
                                        <Text 
                                            fontSize={isMobile ? "xl" : "2xl"} 
                                            fontWeight="bold" 
                                            color={item.color}
                                        >
                                            {item.value}
                                        </Text>
                                        <Text fontSize="sm" color={mutedTextColor} fontWeight="medium">
                                            customers
                                        </Text>
                                    </HStack>
                                    <Badge 
                                        colorScheme="blue" 
                                        fontSize={isMobile ? "xs" : "sm"}
                                        px={3}
                                        py={1}
                                        borderRadius="full"
                                    >
                                        {item.percentage.toFixed(1)}%
                                    </Badge>
                                </VStack>
                            </Flex>
                        </Box>
                    ))}
                </VStack>
            </Box>
        );
    };

    // Render segments chart
    const renderSegmentsChart = () => {
        if (!insightsData || !insightsData.segments || insightsData.segments.length === 0) {
            return (
                <Flex justify="center" align="center" py={10}>
                    <Text color={mutedTextColor}>No segment data available.</Text>
                </Flex>
            );
        }

        const segmentData = insightsData.segments
            .sort((a, b) => b.count - a.count)
            .map((item, index) => ({
                name: item.segment,
                count: item.count,
                percentage: item.percentage,
                color: SEGMENT_COLORS[index % SEGMENT_COLORS.length],
            }));

        return (
            <Box>
                {/* Visual blocks representation */}
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                    {segmentData.map((segment, index) => (
                        <Card
                            key={index}
                            bg={segment.color}
                            borderRadius="lg"
                            boxShadow="md"
                            transition="all 0.2s"
                            _hover={{
                                transform: 'translateY(-4px)',
                                boxShadow: 'xl',
                            }}
                            cursor="pointer"
                        >
                            <CardBody>
                                <VStack align="start" spacing={2}>
                                    <Heading size="sm" color="white" noOfLines={2} minH="40px">
                                        {segment.name}
                                    </Heading>
                                    <HStack justify="space-between" w="100%">
                                        <VStack align="start" spacing={0}>
                                            <Text fontSize="2xl" fontWeight="bold" color="white">
                                                {segment.count}
                                            </Text>
                                            <Text fontSize="xs" color="whiteAlpha.900">
                                                customers
                                            </Text>
                                        </VStack>
                                        <Badge colorScheme="whiteAlpha" fontSize="lg" px={3} py={1}>
                                            {segment.percentage.toFixed(1)}%
                                        </Badge>
                                    </HStack>
                                </VStack>
                            </CardBody>
                        </Card>
                    ))}
                </SimpleGrid>
            </Box>
        );
    };

    // Calculate summary statistics
    const calculateSummaryStats = () => {
        if (!insightsData) return null;

        const topBias = insightsData.bias_ranking?.[0];
        const largestSegment = insightsData.segments?.reduce((max, seg) => 
            seg.count > (max?.count || 0) ? seg : max
        , null);
        const largestAgeGroup = insightsData.age_distribution?.reduce((max, age) => 
            age.count > (max?.count || 0) ? age : max
        , null);

        return {
            totalCustomers: insightsData.total_customers,
            topBias,
            largestSegment,
            largestAgeGroup,
        };
    };

    const stats = calculateSummaryStats();

    return (
        <Container maxW="container.xl" py={6}>
            <VStack spacing={6} align="stretch">
                {/* Header */}
                <Box>
                    <Heading size={isMobile ? 'md' : 'lg'} color={headingTextColor} mb={2}>
                        Portfolio Customer Insights
                    </Heading>
                    <Text color={bodyTextColor} fontSize="sm">
                        Comprehensive overview of your portfolio's customer demographics, segments, and behavioral biases
                    </Text>
                </Box>

                {/* Refresh Button */}
                <Card 
                    bg={cardBg} 
                    borderRadius="lg" 
                    boxShadow="md" 
                    borderWidth="1px" 
                    borderColor={borderColor}
                >
                    <CardBody>
                        <Flex justify="space-between" align="center">
                            <Text fontSize="sm" color={bodyTextColor}>
                                Last updated: {insightsData ? new Date().toLocaleString() : 'Never'}
                            </Text>
                            <Button
                                size="sm"
                                leftIcon={<RepeatIcon />}
                                bg={primaryColor}
                                color="white"
                                onClick={fetchInsightsData}
                                isLoading={loading}
                                _hover={{ bg: buttonHoverBg }}
                            >
                                Refresh Data
                            </Button>
                        </Flex>
                    </CardBody>
                </Card>

                {/* Loading State */}
                {loading && (
                    <Flex justify="center" align="center" py={20}>
                        <VStack spacing={4}>
                            <Spinner size="xl" color={primaryColor} thickness="4px" />
                            <Text color={bodyTextColor}>Loading customer insights...</Text>
                        </VStack>
                    </Flex>
                )}

                {/* Results */}
                {!loading && insightsData && (
                    <>
                        {/* Summary Stats */}
                        {stats && (
                            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                                <Card
                                    bg={infoBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    transition="all 0.2s"
                                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <Stat>
                                            <StatLabel color={bodyTextColor} fontSize="sm">
                                                Total Customers
                                            </StatLabel>
                                            <StatNumber color={isDarkTheme ? accentColor : primaryColor} fontSize={isMobile ? 'xl' : '2xl'}>
                                                {stats.totalCustomers}
                                            </StatNumber>
                                            <StatHelpText color={mutedTextColor}>In Portfolio</StatHelpText>
                                        </Stat>
                                    </CardBody>
                                </Card>

                                {stats.topBias && (
                                    <Card
                                        bg={errorBg}
                                        borderRadius="lg"
                                        boxShadow="md"
                                        transition="all 0.2s"
                                        _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                                        borderWidth="1px"
                                        borderColor={borderColor}
                                    >
                                        <CardBody>
                                            <Stat>
                                                <StatLabel color={bodyTextColor} fontSize="sm">
                                                    Top Bias
                                                </StatLabel>
                                                <StatNumber
                                                    color={errorColor}
                                                    fontSize={isMobile ? 'sm' : 'md'}
                                                    noOfLines={1}
                                                >
                                                    {BIAS_CODES[stats.topBias.bias_code] || stats.topBias.bias_name}
                                                </StatNumber>
                                                <StatHelpText color={mutedTextColor}>
                                                    Score: {stats.topBias.mean_score.toFixed(1)}
                                                </StatHelpText>
                                            </Stat>
                                        </CardBody>
                                    </Card>
                                )}

                                {stats.largestSegment && (
                                    <Card
                                        bg={successBg}
                                        borderRadius="lg"
                                        boxShadow="md"
                                        transition="all 0.2s"
                                        _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                                        borderWidth="1px"
                                        borderColor={borderColor}
                                    >
                                        <CardBody>
                                            <Stat>
                                                <StatLabel color={bodyTextColor} fontSize="sm">
                                                    Largest Segment
                                                </StatLabel>
                                                <StatNumber
                                                    color={successColor}
                                                    fontSize={isMobile ? 'sm' : 'md'}
                                                    noOfLines={1}
                                                >
                                                    {stats.largestSegment.segment}
                                                </StatNumber>
                                                <StatHelpText color={mutedTextColor}>
                                                    {stats.largestSegment.count} customers ({stats.largestSegment.percentage.toFixed(1)}%)
                                                </StatHelpText>
                                            </Stat>
                                        </CardBody>
                                    </Card>
                                )}

                                {stats.largestAgeGroup && (
                                    <Card
                                        bg={purpleBg}
                                        borderRadius="lg"
                                        boxShadow="md"
                                        transition="all 0.2s"
                                        _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                                        borderWidth="1px"
                                        borderColor={borderColor}
                                    >
                                        <CardBody>
                                            <Stat>
                                                <StatLabel color={bodyTextColor} fontSize="sm">
                                                    Largest Age Group
                                                </StatLabel>
                                                <StatNumber color={purpleColor} fontSize={isMobile ? 'xl' : '2xl'}>
                                                    {stats.largestAgeGroup.bucket}
                                                </StatNumber>
                                                <StatHelpText color={mutedTextColor}>
                                                    {stats.largestAgeGroup.count} customers ({stats.largestAgeGroup.percentage.toFixed(1)}%)
                                                </StatHelpText>
                                            </Stat>
                                        </CardBody>
                                    </Card>
                                )}
                            </SimpleGrid>
                        )}

                        {/* Bias Severity Pyramid and Age Distribution side by side */}
                        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                            {/* Bias Ranking Pyramid */}
                            <Card 
                                bg={cardBg} 
                                borderRadius="lg" 
                                boxShadow="md" 
                                borderWidth="1px" 
                                borderColor={borderColor}
                            >
                                <CardBody>
                                    <Heading size={isMobile ? 'sm' : 'md'} mb={2} color={headingTextColor}>
                                        Bias Severity Pyramid
                                    </Heading>
                                    <Text fontSize="sm" color={bodyTextColor} mb={6}>
                                        Customer biases ranked by mean score - highest severity at top
                                    </Text>
                                    {renderBiasPyramid()}
                                </CardBody>
                            </Card>

                            {/* Age Distribution */}
                            <Card 
                                bg={cardBg} 
                                borderRadius="lg" 
                                boxShadow="md" 
                                borderWidth="1px" 
                                borderColor={borderColor}
                            >
                                <CardBody>
                                    <Heading size={isMobile ? 'sm' : 'md'} mb={4} color={headingTextColor}>
                                        Age Distribution
                                    </Heading>
                                    <Text fontSize="sm" color={bodyTextColor} mb={4}>
                                        Customer age breakdown across all segments
                                    </Text>
                                    {renderAgeDistribution()}
                                </CardBody>
                            </Card>
                        </SimpleGrid>

                        {/* Quick Stats */}
                        <Card 
                            bg={cardBg} 
                            borderRadius="lg" 
                            boxShadow="md" 
                            borderWidth="1px" 
                            borderColor={borderColor}
                        >
                            <CardBody>
                                <Heading size={isMobile ? 'sm' : 'md'} mb={4} color={headingTextColor}>
                                    Quick Stats
                                </Heading>
                                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                                    {insightsData.bias_ranking && (
                                        <Box p={4} bg={placeholderBg} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                                            <Text fontSize="sm" fontWeight="bold" color={labelTextColor} mb={2}>
                                                Bias Coverage
                                            </Text>
                                            <Text fontSize="2xl" fontWeight="bold" color={isDarkTheme ? accentColor : primaryColor}>
                                                {insightsData.bias_ranking.length}
                                            </Text>
                                            <Text fontSize="xs" color={bodyTextColor}>
                                                behavioral biases tracked
                                            </Text>
                                        </Box>
                                    )}
                                    {insightsData.segments && (
                                        <Box p={4} bg={placeholderBg} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                                            <Text fontSize="sm" fontWeight="bold" color={labelTextColor} mb={2}>
                                                Customer Segments
                                            </Text>
                                            <Text fontSize="2xl" fontWeight="bold" color={successColor}>
                                                {insightsData.segments.length}
                                            </Text>
                                            <Text fontSize="xs" color={bodyTextColor}>
                                                distinct segments identified
                                            </Text>
                                        </Box>
                                    )}
                                    {insightsData.age_distribution && (
                                        <Box p={4} bg={placeholderBg} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                                            <Text fontSize="sm" fontWeight="bold" color={labelTextColor} mb={2}>
                                                Age Groups
                                            </Text>
                                            <Text fontSize="2xl" fontWeight="bold" color={purpleColor}>
                                                {insightsData.age_distribution.length}
                                            </Text>
                                            <Text fontSize="xs" color={bodyTextColor}>
                                                age brackets analyzed
                                            </Text>
                                        </Box>
                                    )}
                                </SimpleGrid>
                            </CardBody>
                        </Card>

                        {/* Customer Segments */}
                        <Card 
                            bg={cardBg} 
                            borderRadius="lg" 
                            boxShadow="md" 
                            borderWidth="1px" 
                            borderColor={borderColor}
                        >
                            <CardBody>
                                <Heading size={isMobile ? 'sm' : 'md'} mb={2} color={headingTextColor}>
                                    Customer Segments
                                </Heading>
                                <Text fontSize="sm" color={bodyTextColor} mb={6}>
                                    Distribution of customers across different behavioral and demographic segments
                                </Text>
                                {renderSegmentsChart()}
                            </CardBody>
                        </Card>
                    </>
                )}

                {/* No Data State */}
                {!loading && !insightsData && (
                    <Card 
                        bg={cardBg} 
                        borderRadius="lg" 
                        boxShadow="md" 
                        borderWidth="1px" 
                        borderColor={borderColor}
                    >
                        <CardBody>
                            <VStack py={10} spacing={4}>
                                <Text fontSize="lg" color={mutedTextColor} fontWeight="medium">
                                    No insights data available
                                </Text>
                                <Text fontSize="sm" color={mutedTextColor} textAlign="center">
                                    Click refresh to load customer insights
                                </Text>
                            </VStack>
                        </CardBody>
                    </Card>
                )}
            </VStack>
        </Container>
    );
}

export default DashboardPortfolioCustomerInsightsPaid;